import * as R from 'ramda'
// React
import { useCallback, useEffect, useState } from 'react'
import { LIMITS } from '@weblite/permission'
// Material
import {
  alpha,
  Backdrop,
  Box,
  Card,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { Gender, Location } from '@weblite/sahab-profile-action/profile'
import {
  getSectionsHistoryTop,
  useActiveSectionsTop,
  useHistoryCustomBack,
} from 'weblite-router'
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace'
import { AlertCircleIcon } from '@weblite/icons'
// Common
import { Label } from '$common/label'
import { TextInput } from '$common/input'
import { nothing } from '$func/utils'
import { AuthHeader } from '$common/auth-header/auth-header'
import { PrimaryButton, SelectButton } from '$common/buttons'
import { showSelectCounty, showSelectGender } from '$comp/modals/select-menu'
import { dateFormatter } from '$func/time'
import { showBirthdatePickerModal } from '$comp/modals/date-picker'
import {
  editProfile,
  getReadableLocation,
  MADRESE_TAALI_MOGHAVEMAT_ROOMID,
} from '$logic/profile'
import useDebouncedCallback from '$hooks/use-debounced-callback'
import { updateClientUser, useClientUser } from '$logic/client-user'
import {
  showInfoSuccessfullyUpdatedSnackbar,
  showSignupEndedSuccessfullySnackbar,
} from '$comp/snackbars'
import { BottomDrawer } from '$common/bottom-drawer'
import { closeProfileCompletion } from '$logic/router'
import { usePrevious } from '$common/react-hooks/use-previous'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { useFormData } from '$hooks/use-form-data'
import { t } from '$i18n'
import { useProfile } from '$logic/profile/react-hook'
import { ArrowRightIcon } from '$common/icons'
import { HallAvatar } from '$common/avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { areEqualStr, toEnglishNumber, toLocale } from '$func/text'
import { SelectWithChips } from '$common/select-with-chips'
import { NETWORK_OPTIONS } from '$comp/modals/profile-completion/profile-completion.data'

const { userConstraints } = LIMITS
const NATIONAL_ID_LENGTH = 10

const ACTION_BUTTON_HEIGHT = 72
const HEADER_HEIGHT = 28
const JUST_USE_LETTERS = 'justUseLetters'

const nameSelectionRegex =
  /^[A-Za-z\u0621-\u064A\u066E-\u06D3\u06FA-\u06FC\uFB50-\uFDFF\uFE70-\uFEFF\u200C\u0020]+$/
// Note: check for persian or english letter
// English Characters: [A-Za-z] matches all English letters.
//   Arabic and Persian Letters:
//   Shared Arabic/Persian characters.
//   Additional Persian-specific and Arabic-specific characters.
// Space (\u0020): Matches the universal space character.
//   Half-Space (\u200C): Persian-specific formatting character.

type ProfileCompletionData = {
  firstname: string
  lastname?: string
  nationalId: string
  nationalIdError: string
  fatherName: string
  birthDate: string
  gender: Gender | undefined
  location?: Location | undefined
  networks: number[]
  nameError?: {
    error: string
    item: string
  }
}

const initialValue = {
  firstname: '',
  lastname: '',
  nationalId: '',
  nationalIdError: '',
  fatherName: '',
  birthDate: '',
  gender: undefined,
  location: undefined,
  networks: [],
  nameError: {
    error: '',
    item: '',
  },
} as ProfileCompletionData

// #moghavemat
export function ProfileCompletionModal() {
  const clientUser = useClientUser()
  const {
    nationalId: prevNationalId,
    firstname: previousFirstname,
    lastname: previousLastname,
  } = clientUser

  const profile = useProfile()

  const { items, setItem, setItems } = useFormData<ProfileCompletionData>({
    ...initialValue,
    nationalId: prevNationalId || '',
    firstname: previousFirstname || '',
    lastname: previousLastname || '',
  })

  const sendItemsAnalyticsDebounced = useDebouncedCallback(
    (itemsArgs: ProfileCompletionData) => {
      const sectionsTop = getSectionsHistoryTop()
      if (sectionsTop === 'sjf')
        analite('PROFILE_COMPLETION_DATA_CHANGED', itemsArgs)
    },
    [],
    1500,
    true,
  )

  useEffect(() => {
    sendItemsAnalyticsDebounced(items)
  }, [items])

  useEffect(() => {
    if (clientUser?.firstname)
      setItems(prevItems => ({
        ...prevItems,
        firstname: clientUser.firstname || '',
        lastname: clientUser.lastname,
        nationalId: clientUser.nationalId || '',
      }))
  }, [clientUser])

  useEffect(() => {
    setItems(prevItems => ({
      ...prevItems,
      ...R.omit(['tasks', 'updatedAt', 'extra'], profile),
      ...profile?.extra,
    }))
  }, [profile])

  const {
    firstname,
    lastname,
    networks,
    fatherName,
    birthDate,
    location,
    gender,
    nationalId,
    nationalIdError,
    nameError,
  } = items

  const onChangeNetwork = useCallback(
    (newNetwork: string) => {
      const networkIndex = NETWORK_OPTIONS.findIndex(
        network => network.name === newNetwork,
      )
      setItem('networks', [networkIndex])
    },
    [setItem],
  )

  const editMode = !!profile?.extra?.networks?.[0]

  const [loading, setLoading] = useState(false)

  const disableSubmitButton =
    !firstname ||
    !fatherName ||
    nationalId.length < NATIONAL_ID_LENGTH ||
    !location ||
    !birthDate ||
    !gender ||
    !networks?.length

  const onSignUpClick = async () => {
    setItem('nationalIdError', '')
    const nationalIdInEnglish = toEnglishNumber(nationalId)
    if (nationalIdInEnglish.length !== NATIONAL_ID_LENGTH) {
      setItem('nationalIdError', t(`completeProfile.errors.least-ten-number`))
      return
    }

    const updatedClientUserFields = {
      ...(!areEqualStr(firstname.trim(), previousFirstname) && {
        firstname: firstname.trim(),
      }),
      ...(!areEqualStr(lastname?.trim() || '', previousLastname || '') && {
        lastname: lastname?.trim() || '',
      }),
      ...(!areEqualStr(nationalIdInEnglish || '', prevNationalId || '') && {
        nationalId: nationalIdInEnglish,
      }),
    }

    const updatedProfileFields = {
      ...(!areEqualStr(fatherName.trim(), profile.fatherName || '') && {
        fatherName: fatherName.trim(),
      }),
      ...((location?.county !== profile.location?.county ||
        location?.province !== profile.location?.province) && {
        location,
      }),
      ...(!areEqualStr(birthDate, profile?.birthDate || '') && {
        birthDate,
      }),
      ...(!areEqualStr(gender || '', profile.gender || '') && {
        gender,
      }),
      ...(!areEqualStr(
        networks?.join('') || '',
        profile?.extra?.networks.join('') || '',
      ) && {
        networks,
      }),
    }

    if (Object.keys(updatedClientUserFields).length) {
      const res = await updateClientUser(updatedClientUserFields)
      if (res?.error?.code) {
        analite('SUBMIT_NATIONAL_ID_ERROR', {
          nationalId: nationalIdInEnglish,
          error: res.error.code,
        })

        setItem(
          'nationalIdError',
          t(`completeProfile.errors.${res.error.code}`),
        )

        return
      }
    }

    closeProfileCompletion()

    if (!Object.keys(updatedProfileFields).length) {
      showInfoSuccessfullyUpdatedSnackbar()
      return
    }

    await editProfile({
      gender,
      birthDate,
      location,
      fatherName,
      extra: {
        networks,
      },
    })

    analite('SUBMIT_ADDITIONAL_REGISTRATION')

    if (editMode) showInfoSuccessfullyUpdatedSnackbar()
    else showSignupEndedSuccessfullySnackbar()
  }

  useHistoryCustomBack(() => {}, [])

  const activeSection = useActiveSectionsTop()
  const prevActiveSection = usePrevious(activeSection)

  const isDesktop = useIsDesktopSize()

  const isOpen =
    activeSection === 'sjf' ||
    (prevActiveSection === 'sjf' && activeSection === 'overlay')

  const changeName = useCallback(
    (item: 'firstname' | 'lastname' | 'fatherName', value: string) => {
      setItem('nameError', { error: '', item: '' })
      if (!!value && !nameSelectionRegex.test(value)) {
        setItem('nameError', {
          error: t(`completeProfile.${JUST_USE_LETTERS}`),
          item,
        })
        return
      }
      setItem(item, value)
    },
    [setItem],
  )

  return (
    <AuthContainer isOpen={isOpen}>
      <AuthHeader sx={{ gap: '8px', marginY: '8px' }}>
        {editMode && (
          <IconButton
            edge="end"
            sx={{ color: 'neutral500' }}
            onClick={closeProfileCompletion}
          >
            <ArrowRightIcon sx={{ color: 'neutral900' }} />
          </IconButton>
        )}
        <HallAvatar
          roomId={MADRESE_TAALI_MOGHAVEMAT_ROOMID}
          size={AvatarSize.SM}
        />
        <Typography variant="headerMedium">
          {t('completeProfileModal.title')}
        </Typography>
      </AuthHeader>
      <Divider />
      <InfoBox />
      <Box
        sx={{
          marginTop: '20px',
          marginBottom: 'auto',
          height: isDesktop
            ? `calc(100% - ${ACTION_BUTTON_HEIGHT + HEADER_HEIGHT + 20}px)`
            : 'unset',
          overflow: isDesktop ? 'auto' : 'unset',
          scrollbarWidth: isDesktop ? 'thin' : 'initial',
          padding: '0 1px',
        }}
      >
        <Label title={t('user.name')} required />
        <TextInput
          placeholder={t('user.enterName')}
          value={firstname || ''}
          onChange={newName => changeName('firstname', newName || '')}
          maxLength={userConstraints.firstname.maxLength}
          hasError={nameError?.item === 'firstname'}
          errorMessage={nameError?.error}
        />

        <Label title={t('user.lastName')} />
        <TextInput
          placeholder={t('user.enterLastName')}
          value={lastname || ''}
          onChange={newName => changeName('lastname', newName || '')}
          maxLength={userConstraints.firstname.maxLength}
          hasError={nameError?.item === 'lastname'}
          errorMessage={nameError?.error}
        />

        <Label title={t('completeProfile.fatherName')} required />
        <TextInput
          placeholder={t('completeProfile.enterFatherName')}
          value={fatherName || ''}
          onChange={newName => {
            changeName('fatherName', newName || '')
          }}
          maxLength={userConstraints.firstname.maxLength}
          hasError={nameError?.item === 'fatherName'}
          errorMessage={nameError?.error}
        />

        <Label title={t('completeProfile.nationalCode')} required />
        <TextInput
          placeholder={t('completeProfile.enterNationalCode')}
          value={toLocale(nationalId)}
          onChange={newNationalId =>
            setItem('nationalId', toEnglishNumber(newNationalId))
          }
          hasError={!!nationalIdError}
          errorMessage={nationalIdError}
          inputProps={{ type: 'tel', style: { textAlign: 'right' } }}
          maxLength={NATIONAL_ID_LENGTH}
        />

        <Label title={t('user.location')} required />
        <SelectButton
          value={location ? getReadableLocation(location) : undefined}
          onClick={() =>
            showSelectCounty(newLocation => setItem('location', newLocation))
          }
        />

        <Label title={t('myAccountEdit.birthDate')} required />
        <SelectButton
          value={
            birthDate ? dateFormatter.format(new Date(birthDate)) : undefined
          }
          onClick={() =>
            showBirthdatePickerModal(
              date =>
                // NOTE: Format date to yyyy-mm-dd
                setItem('birthDate', date.toISOString().split('T')[0]),
              birthDate ? new Date(birthDate) : undefined,
            )
          }
        />

        <Label title={t('myAccountEdit.gender')} required />
        <SelectButton
          value={
            gender
              ? gender === Gender.MALE
                ? t('common.male')
                : t('common.female')
              : null
          }
          onClick={() =>
            showSelectGender(newGender => setItem('gender', newGender))
          }
        />

        <Label title={t('completeProfile.network')} required />
        <SelectWithChips
          value={NETWORK_OPTIONS[networks?.[0]] || null}
          options={NETWORK_OPTIONS}
          onChange={newNetwork =>
            newNetwork && onChangeNetwork((newNetwork as { name: string }).name)
          }
          getOptionLabel={option => option.name}
          placeholder={t('completeProfile.enterNetwork')}
        />
      </Box>

      <div
        style={{
          margin: isDesktop ? '12px 0' : '0 0 12px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '72px',
          minHeight: '72px',
        }}
      >
        <PrimaryButton
          fullWidth
          size="medium"
          variant="contained"
          text={editMode ? t('common.edit') : t('common.register')}
          loading={loading}
          disabled={disableSubmitButton || loading}
          sx={{
            backgroundColor: 'primary500',
          }}
          onClick={() => {
            setLoading(true)
            onSignUpClick().finally(() => setLoading(false))
          }}
        />
      </div>
    </AuthContainer>
  )
}

interface Prop {
  children: ReactJSXElement[]
  isOpen: boolean
}

const DesktopAuthContainerRoot = styled('div')`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
`

const DesktopCard = styled(Card)<{ isDesktop: boolean }>`
  border-radius: 16px;
  overflow: hidden;
  margin: auto;
  height: calc(100% - 64px);
  max-width: 400px;
  max-height: 760px;
  width: 400px;
  padding: 0 23px;
  display: flex;
  flex-direction: column;
`

function AuthContainer({ children, isOpen }: Prop) {
  const isDesktop = useIsDesktopSize()
  return isDesktop ? ( // TODO: why drawer disappears after select modal items in desktop
    <DesktopAuthContainerRoot>
      <Backdrop open>
        <DesktopCard isDesktop={isDesktop}>{children}</DesktopCard>
      </Backdrop>
    </DesktopAuthContainerRoot>
  ) : (
    <BottomDrawer
      isOpen={isOpen}
      variant="permanent"
      onClose={nothing}
      isFullscreen
      sx={{ width: '100%', height: '100%' }}
      paperProps={{ sx: { padding: '0 23px', overflow: 'auto' } }}
    >
      {children}
    </BottomDrawer>
  )
}

function InfoBox() {
  const { palette } = useTheme()
  return (
    <div
      style={{
        marginTop: '24px',
        display: 'flex',
        backgroundColor: palette.secondary50,
        padding: '4px 8px',
        gap: '4px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: alpha(palette.neutral1000, 0.1),
      }}
    >
      <AlertCircleIcon
        style={{ marginTop: '2px' }}
        primaryColor={palette.secondary50}
        secondaryColor={palette.secondary700}
      />
      <Typography variant="paragraph2">
        {t('completeProfile.infoBox')}
      </Typography>
    </div>
  )
}
